<template>
  <div :class="field.class || 'col-md-3'" class="col" v-if="!field.hidden">
    <template v-if="field.type === 'input'">
      <b-form-group>
        <label
          class="form__label"
          v-b-tooltip.hover="field.tooltip ? { variant: 'base', placement: 'top', boundary:'viewport', delay: { show: 300, hide: 0 }} : null"
          :title="field.tooltip"
        >{{ field.label }}</label>
        <b-form-input
          :name="field.name"
          :type="field.typeInput || 'text'"
          :placeholder="field.placeholder"
          :disabled="field.disabled"
          v-model="getFieldValue"
          :class="{ 'is-invalid': isInvalidField, 'is-valid': isValidField }"
          debounce="300"
          @keydown="keyDown"
        />
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'range'">
      <b-form-group>
        <label class="form__label">{{ field.label }}</label>
        <b-form-input
          :name="field.name"
          type="range"
          :min="field.min || '0'"
          :max="field.max || '5'"
          v-model="getFieldValue"
          :class="{ 'is-invalid': isInvalidField, 'is-valid': isValidField }"
          debounce="300"
        />
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'range_slider'">
      <b-form-group>
        <label class="form__label">{{ field.label }}</label>
        <vue-range-slider
          :key="`${field.name}-${field.data ? field.data.length : 0}`"
          :style='{"margin-top": "10px"}'
          :data="field.data || null"
          v-model="getFieldValue"
          :name="field.name"
          :disabled="field.disabled"
          :tooltip="false"
          :dot-size="12"
          :process-style='{ "background-color": "#007bff" }'
        ></vue-range-slider>
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'switch'">
      <b-form-group class="switch-form-group">
        <label class="form__label form__label-switch" :for="`${field.name}-switch`">{{ field.label }}</label>
        <b-button
          variant="white"
          class="button btn-icon button-checkbox_sm"
        >
          <b-form-checkbox
            size="sm"
            :name="field.name"
            :id="`${field.name}-switch`"
            switch
            v-model="getFieldValue"
            :disabled="field.disabled"
            class="switch"
          />
        </b-button>
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'checkbox'">
      <b-form-group>
          <span v-if="field.info" v-b-tooltip.hover="{ variant: 'base', title: field.info }" class="mx-3 mb-1 material-icons text-primary">
            info
          </span>
          <a target="_blank" v-if="field.labelLinkTo" :href="field.labelLinkTo" class="form__label cursor-pointer text-primary ml-1">
            {{ field.labelLink }}
          </a>
          <label :for="field.name" class="form__label cursor-pointer">{{ field.label }}</label>
          <b-form-checkbox :id="field.name" :disabled="field.disabled" v-model="getFieldValue" />
          <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'multiselect'">
      <b-form-group>
        <label class="form__label">{{ field.label }}</label>
        <TagsMultiselect :field="field" :value="getFieldValue" :validation="validation" @input="updateMultiselectValue" />
        <!-- <multiselect
          v-model="getFieldValue"
          :allow-empty="field.allowEmpty"
          :placeholder="field.placeholder"
          :tag-placeholder="field.placeholder"
          :options="field.options"
          :track-by="field.valueField"
          :label="field.selectLabel"
          :multiple="true"
          :disabled="field.disabled"
          :showLabels="false"
          :limit="1"
          :name="field.name"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <span class="multiselect__single" v-if="values.length && !isOpen">
              {{ values.length }}/{{ field.options.length }}
            </span>
          </template>
          <template slot="option" slot-scope="props" v-if-="field.labelClass && field.labelClass !== ''">
            <div class="option__desc">
              <span class="option__title" :class="field.labelClass">{{ field.selectLabel ? props.option[field.selectLabel] : props.option }}</span>
            </div>
          </template>
          <template v-slot:tag="{ option, remove }">
            <span style="display: inline-flex; align-items: center; background-color: #fff; border-radius: 3px; color: #3D5170; padding: 2px 4px; height: 16px; margin-right: 2px;">
              <span>{{ field.selectLabel ? option[field.selectLabel] : option }}</span>
              <span style="margin-left: 2px; cursor: pointer;" @click.prevent @mousedown.prevent.stop="remove(option, $event)" class="material-icons"> close </span>
            </span>
          </template>
        </multiselect> -->
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'select'">
      <b-form-group>
        <label class="form__label">{{ field.label }}</label>
        <div v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title="getFieldValue ? (field.selectLabel ? getFieldValue[field.selectLabel] : getFieldValue) : null">
          <multiselect
            v-model="getFieldValue"
            :options="field.options"
            :placeholder="field.placeholder"
            :track-by="field.valueField"
            :label="field.selectLabel"
            :disabled="field.disabled"
            :allow-empty="field.allowEmpty ? field.allowEmpty : false"
            :showLabels="false"
            :name="field.name"
            :class="{ 'multiselect__single-line': true, 'is-invalid': isInvalidField, 'is-valid': isValidField }"
          >
            <template slot="option" slot-scope="props" v-if-="field.labelClass && field.labelClass !== ''">
              <div class="option__desc">
                <span class="option__title" :class="field.labelClass">{{ field.selectLabel ? props.option[field.selectLabel] : props.option }}</span>
              </div>
            </template>
          </multiselect>
        </div>
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'datepicker'">
      <b-form-group style="position: relative;">
        <label class="form__label">{{ field.label }}</label>
        <!-- TODO NEED UPDATE VUE BOOTSTRAP AND REMOVE CURRENT DATAPICKER, INPUT -->
        <b-form-input
          style="position: absolute;height: 32px;"
          :value="convertDataToString(getFieldValue)"
          :class="{ 'is-invalid': isInvalidField, 'is-valid': isValidField }"
        />
        <Datepicker
          :bootstrap-styling="true"
          :disabledDates="field.disabledDates"
          :placeholder="field.placeholder"
          :format="field.format"
          :minimum-view="field.minimumView"
          :disabled="field.disabled"
          class="datapicker"
          :name="field.name"
          :value="moment(getFieldValue).toISOString()"
          @input="getFieldValue = _toLocalTime($event)"
        />
        <slot name="errors"></slot>
      </b-form-group>
    </template>
    <template v-if="field.type === 'component'">
      <component
        v-model="getFieldValue"
        v-on="field.action"
        v-bind:is="field.component"
        v-bind="field"
      >
      </component>
    </template>
    <template v-if="field.type === 'slot'">
      <b-form-group>
        <slot :data="field" :fields="fields" :updateField="updateSlotField"></slot>
        <slot name="errors"></slot>
      </b-form-group>
    </template>
  </div>
</template>

<script>
import Moment from 'moment'
import Datepicker from 'vuejs-datepicker'
import VueRangeSlider from 'vue-range-component-fixed'
import 'vue-range-component-fixed/dist/vue-range-slider.css'
import TagsMultiselect from '@/components/TagsMultiselect'

export default {
  name: 'FormGeneratorItems',
  components: {
    Datepicker,
    VueRangeSlider,
    TagsMultiselect
  },
  data () {
    return {
      fields: {},
      wasInvalid: false
    }
  },
  props: {
    field: {
      type: Object,
      default: () => {}
    },
    value: {},
    validation: {
      type: Object,
      default: null
    }
  },
  watch: {
    'validation.value.$error': {
      handler (isCurrentlyInvalid) {
        if (isCurrentlyInvalid) {
          this.wasInvalid = true
        }
      },
      immediate: true
    }
  },
  computed: {
    getFieldValue: {
      get: function () {
        return this.value
      },
      set: function (newValue) {
        this.$emit('input', newValue)
        this.$emit('update', this.$vnode.key, { ...this.field, value: newValue })
      }
    },
    moment () {
      return Moment
    },
    isInvalidField () {
      return this.validation && this.validation.value && this.validation.value.$error
    },
    isValidField () {
      return this.validation && this.validation.value && this.validation.value.$dirty && !this.validation.value.$error && this.wasInvalid
    }
  },
  methods: {
    convertDataToString (value) {
      if (value) return value.toString()
    },
    keyDown (event) {
      this.$emit('keyDown', event)
    },
    updateMultiselectValue (value) {
      this.$emit('input', value)
      this.$emit('update', this.$vnode.key, { ...this.field, value: value })
    },
    updateSlotField (value) {
      this.field.value = value
      this.$emit('input', value)
      this.$emit('update', this.$vnode.key, { ...this.field, value: value })
    }
  }
}
</script>

<style lang="scss" scoped>
.invoices-filters-field-wrapper {
  flex: 0 0 25%;
  max-width: 25%;
}

@media (min-width: 1300px) {
  .invoices-filters-field-wrapper {
    flex: 0 0 20.01%;
    max-width: 20.01%;
  }
}

.switch-form-group {
  ::v-deep div {
    justify-content: flex-end;
    display: flex;
    flex-direction: row-reverse;

    label {
      margin-bottom: 0;
      font-size: 0.8125rem;
    }

    button {
      background: transparent !important;
      margin-right: 5px;
      border: none;
      cursor: auto !important;
    }
  }
}

.form__label-switch {
  color: $fontColorBase;
  cursor: pointer;
}

.single-select-with-ellipsis {
  ::v-deep .multiselect {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }

  ::v-deep .multiselect__tags {
    min-height: 32px;
    padding-right: 30px !important;
    overflow: hidden !important;
  }

  ::v-deep .multiselect__single {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    display: block !important;
  }

  ::v-deep .multiselect__select {
    width: 40px;
    height: 32px;
    top: 0px;
    right: 0px;
  }

  ::v-deep .multiselect__content-wrapper {
    width: 100%;
  }

  ::v-deep .multiselect__input {
    max-width: calc(100% - 30px) !important;
  }
}
</style>
